import {
  Button,
  DayPickerInput,
  Form,
  Icon,
  InlineTooltip,
  SingleSelect,
  TextArea,
  TimeInput,
  Tooltip,
} from '~/components';
import ReadForm from '~/components/read-only/ReadForm';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import RejectionMessage from '~/components/RejectionMessage';
import { useApi, useIntegrations, useWorkspace, useMember } from '~/contexts';
import { useTimeZoneName } from '~/hooks';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '~/styles';
import { Field, ProjectSelect, RoleSelect, TaskSelect, TimeOffTypeSelect } from '../fields';
import { useTimesheets } from '../timesheets/TimesheetContext';
import TaskDetails from './TaskDetails';
import { dateFormats } from '~/utils';
import StartTimeInput from './StartTimeInput';
import EndTimeInput from './EndTimeInput';
import MicrosoftCalendarEventPicker from './MicrosoftCalendarEventPicker';

// isActive is only used by `styled` and should not be passed through
// eslint-disable-next-line no-unused-vars
const StyledButton = ({ isActive, ...props }) => <Button {...props} />;

const TimeContainer = styled.div`
  display: flex;
  align-items: center;
`;

const PlayPauseButton = styled(StyledButton)`
  flex-shrink: 0;
  width: 1.875rem;
  height: 1.875rem;
  padding: 0;
  padding-left: ${({ isActive }) => (isActive ? '0' : '0.125rem')};
  margin-left: 0.625rem;
  color: ${({ isActive }) => (isActive ? colors.white : colors.primary)};
  font-size: 0.75rem;
  background-color: ${({ isActive }) => (isActive ? colors.primary : colors.grey5)};
  position: relative;

  &&:disabled {
    color: ${colors.grey25};
    background-color: ${colors.grey5};
  }

  &:hover,
  &:focus {
    color: ${colors.white};
    background-color: ${({ isActive }) => (isActive ? colors.accent : colors.primary)};
  }
`;

export default function GeneralTab({
  form,
  entry,
  project,
  role,
  task,
  memberId,
  member,
  onPlayPause,
  isNew,
  isOwner,
  drawerLoaded,
  rejectedHistoryItem,
  isProjectDisabled = false,
}) {
  const { workspace } = useWorkspace();
  const api = useApi();

  const { values, errors, conditionals, setFieldValue } = form;
  const {
    date,
    minutes,
    typeId,
    timeOffTypeId,
    projectId,
    projectRoleId,
    projectTaskId,
    notes,
    statusId,
    startTime,
    endTime,
    microsoftEventId,
  } = values;

  const timeInputRef = useRef();
  const { member: currentMember } = useMember();

  const appliedByEvent = useRef({});
  const canLogTimeOff = !!member?.timeOffAllowed;
  const timezone = useTimeZoneName(currentMember?.timeZoneId || workspace.timeZoneId);

  const integrations = useIntegrations();
  const hasCalendarPicker = isOwner && (!!integrations?.microsoft || !!entry?.microsoftEventId);
  const [timeOffTypes, setTimeOffTypes] = useState();

  useEffect(() => {
    if (!hasCalendarPicker || !canLogTimeOff) return;

    let cancelled = false;

    const fetchTimeOffTypes = async () => {
      let types = [];
      try {
        const { data } = await api.www.workspaces(workspace.id).timeEntries().timeOffTypes({ memberId });
        types = data ?? [];
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        if (!cancelled) setTimeOffTypes(types);
      }
    };

    fetchTimeOffTypes();

    return () => {
      cancelled = true;
    };
  }, [api, workspace.id, memberId, hasCalendarPicker, canLogTimeOff]);

  useEffect(() => {
    if (isNew && drawerLoaded && timeInputRef.current) {
      timeInputRef.current.focus();
    }
  }, [isNew, drawerLoaded]);

  const timesheetContext = useTimesheets();
  const timesheet = useMemo(
    () => _.find(timesheetContext.timesheets, (ts) => ts.id === entry?.timesheetId),
    [timesheetContext, entry?.timesheetId],
  );

  const getDayProps = useCallback(
    (day) => {
      if (
        values.typeId === 'project_time' &&
        workspace.timeAndExpensesWithinProjectDates &&
        ((project?.start && !moment(day).isSameOrAfter(project?.start, 'day')) ||
          (project?.end && !moment(day).isSameOrBefore(project?.end, 'day')))
      ) {
        return {
          disabled: true,
          tooltip: `This day is outside of the project's start and end dates.`,
        };
      }

      if (
        workspace.timeAndExpensesWithinMemberDates &&
        ((member?.activeStartDate && !moment(day).isSameOrAfter(member?.activeStartDate, 'day')) ||
          (member?.activeEndDate && !moment(day).isSameOrBefore(member?.activeEndDate, 'day')))
      ) {
        return {
          disabled: true,
          tooltip: `This day is outside of the member's start and end dates.`,
        };
      }

      if (timesheetContext.useTimesheets) {
        if (timesheet) {
          const disabled = !moment(day).isBetween(timesheet.start, timesheet.end, 'day', '[]');

          return {
            disabled,
            tooltip: disabled ? `This day is outside this time entry's timesheet period.` : null,
          };
        } else {
          const disabled = _.some(timesheetContext.timesheets, (ts) =>
            moment(day).isBetween(ts.start, ts.end, 'day', '[]'),
          );

          return {
            disabled,
            tooltip: disabled ? 'Timesheet has been submitted.' : null,
          };
        }
      }
    },
    [timesheetContext, timesheet, values, project, member, workspace],
  );

  const handleStartTimeChange = (value) => {
    let newValue;
    if (
      !!value &&
      !!endTime &&
      endTime !== '00:00' &&
      moment(value, dateFormats.shortTime).isAfter(moment(endTime, dateFormats.shortTime))
    ) {
      newValue = endTime;
    } else {
      newValue = value;
    }

    setFieldValue('startTime', newValue);

    // adjust timerStartedAt when updating startTime and timer is running
    if (entry?.timerStartedAt && !endTime) {
      const parsedStart = moment(newValue, dateFormats.shortTime);
      if (!parsedStart.isValid()) return;

      const startedAt = moment(entry.timerStartedAt);
      startedAt.set({
        hour: parsedStart.hour(),
        minute: parsedStart.minute(),
        second: 0,
        millisecond: 0,
      });

      entry.timerStartedAt = startedAt.toISOString();
    }

    if (value && endTime) {
      let computed = moment(endTime, dateFormats.shortTime).diff(moment(newValue, dateFormats.shortTime), 'minutes');

      if (minutes !== computed) {
        setFieldValue('minutes', computed);
      }
    }
  };

  const handleEndTimeChange = (value) => {
    if (value) {
      const start = moment(startTime, dateFormats.shortTime);
      let end = moment(value, dateFormats.shortTime);

      if (end.isBefore(start) && value !== '00:00') {
        const noon = end.clone().startOf('day').add(12, 'hours');
        // If it’s before 12:00pm (noon), add 12 hours to the end time, else snap to startTime
        end = end.isBefore(noon) ? end.clone().add(12, 'hours') : start;
      }

      value = end.format(dateFormats.shortTime);
    }

    setFieldValue('endTime', value);

    if (value !== '' && startTime !== '') {
      let computed = moment(value, dateFormats.shortTime).diff(moment(startTime, dateFormats.shortTime), 'minutes');

      if (value === '00:00') computed = computed + 1440;

      if (minutes !== computed) {
        setFieldValue('minutes', computed);
      }
    }
  };

  const memberDuration = member.timeTrackingMethods?.duration;
  const memberTimeRange = member.timeTrackingMethods?.timeRange;
  const trackTimeByDuration = workspace.trackTimeByDuration && memberDuration;
  const trackTimeByTimeRange = workspace.trackTimeByTimeRange && memberTimeRange;
  const timerAction = !trackTimeByDuration ? 'Stop' : 'Pause';
  const disableMinutesInput =
    !trackTimeByDuration || entry?.timerStartedAt || (trackTimeByTimeRange && (values.startTime || values.endTime));

  const disablePlayPauseButton =
    conditionals.disableTimer ||
    (trackTimeByTimeRange && (values.endTime || values.startTime) && !entry?.timerStartedAt);

  let durationTooltipMessage;
  if (entry?.timerStartedAt) durationTooltipMessage = `${timerAction} the timer to update details of this time entry.`;
  else if (!trackTimeByDuration && !trackTimeByTimeRange) durationTooltipMessage = 'You cannot track time.';
  else if (!trackTimeByDuration)
    durationTooltipMessage = 'You can only track time by entering start and end times or using a timer.';

  const handlePickCalendarEvent = (event) => {
    const isTimeOff = event.type === 'time_off';

    setFieldValue('microsoftEventId', event.id);
    setFieldValue('microsoftSeriesId', event.microsoftSeriesId || '');
    if (event.notes) setFieldValue('notes', event.notes);

    setFieldValue('typeId', isTimeOff ? 'time_off' : 'project_time');

    const applied = {};
    // The end follows the prefilled amount, which time off caps at the working day.
    if (trackTimeByTimeRange && event.startTime && event.endTime) {
      const eventEndTime = moment(event.startTime, 'HH:mm').add(event.minutes, 'minutes').format('HH:mm');
      setFieldValue('startTime', event.startTime);
      setFieldValue('endTime', eventEndTime);
      applied.startTime = event.startTime;
      applied.endTime = eventEndTime;
    }
    if (event.minutes != null) {
      setFieldValue('minutes', event.minutes);
      applied.minutes = event.minutes;
    }
    if (isTimeOff && !timeOffTypeId && timeOffTypes?.length === 1) {
      setFieldValue('timeOffTypeId', timeOffTypes[0].id);
      applied.timeOffTypeId = timeOffTypes[0].id;
    }
    appliedByEvent.current = applied;

    if (!isTimeOff && event.suggestedProjectId) {
      setFieldValue('projectId', event.suggestedProjectId);
      setFieldValue('projectRoleId', event.suggestedProjectRoleId || '');
      setFieldValue('projectTaskId', event.suggestedProjectTaskId || '');
    }
  };

  const handleClearCalendarEvent = () => {
    const applied = appliedByEvent.current;
    appliedByEvent.current = {};

    setFieldValue('microsoftEventId', '');
    setFieldValue('microsoftSeriesId', '');

    if (applied.startTime === startTime) setFieldValue('startTime', '');
    if (applied.endTime === endTime) setFieldValue('endTime', '');
    if (applied.minutes === minutes) setFieldValue('minutes', '');
    if (applied.timeOffTypeId === timeOffTypeId) setFieldValue('timeOffTypeId', '');
  };

  return (
    <div>
      <Form.Section title="Entry Details">
        {entry?.statusId === 'rejected' && rejectedHistoryItem && (
          <RejectionMessage historyItem={rejectedHistoryItem}></RejectionMessage>
        )}

        {conditionals.member && (
          <ReadForm.Control>
            <ReadTextbox label="Member" value={conditionals.member} />
          </ReadForm.Control>
        )}

        {conditionals.statusRights && (
          <Form.Control>
            <Field name="statusId" errors={errors}>
              {entry?.timerStartedAt ? (
                <Tooltip message={`${timerAction} the timer to update the status of this time entry.`}>
                  <SingleSelect name="statusId" placeholder="Approval Status" value={statusId} disabled>
                    <option value="not_submitted">Not Submitted</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="rejected">Rejected</option>
                    <option value="approved">Approved</option>
                  </SingleSelect>
                </Tooltip>
              ) : (
                <SingleSelect
                  name="statusId"
                  placeholder="Approval Status"
                  value={statusId}
                  disabled={conditionals.statusRights !== 'write'}
                  onChange={(event) => setFieldValue('statusId', event.target.value)}>
                  <option value="not_submitted" disabled={timesheetContext.useTimesheets && !!entry?.timesheetId}>
                    Not Submitted
                  </option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="rejected">Rejected</option>
                  <option value="approved">Approved</option>
                </SingleSelect>
              )}
            </Field>
          </Form.Control>
        )}

        <Form.Control>
          <Field name="date" errors={errors}>
            <DayPickerInput
              name="date"
              placeholder="Date"
              clearable={false}
              value={date}
              getDayProps={getDayProps}
              onChange={(value) => setFieldValue('date', value)}
            />
          </Field>
        </Form.Control>
        {trackTimeByTimeRange && (
          <Form.Control>
            <Field name="startTime" errors={errors}>
              <Tooltip
                message={
                  !!entry?.timerStartedAt &&
                  !entry?.startTime &&
                  'Pause the timer to update details of this time entry.'
                }>
                <StartTimeInput
                  name="startTime"
                  value={startTime}
                  disabled={!!entry?.timerStartedAt && !entry?.startTime}
                  onChange={(value) => handleStartTimeChange(value)}
                  materialPlaceholder="Start Time"
                />
              </Tooltip>
            </Field>
            <Field name="endTime" errors={errors}>
              <Tooltip
                message={!!entry?.timerStartedAt && `${timerAction} the timer to update details of this time entry.`}>
                <EndTimeInput
                  name="endTime"
                  disabled={!!entry?.timerStartedAt}
                  entry={entry}
                  value={endTime}
                  onChange={(value) => handleEndTimeChange(value)}
                  materialPlaceholder="End Time"
                />
              </Tooltip>
            </Field>
          </Form.Control>
        )}
        <Form.Control>
          <Field name="minutes" errors={errors}>
            <TimeContainer>
              <Tooltip message={durationTooltipMessage}>
                <TimeInput
                  ref={timeInputRef}
                  materialPlaceholder="Duration"
                  name="minutes"
                  placeholder={(currentMember?.useDecimalTimeEntry ?? workspace?.useDecimalTimeEntry) ? '0.00' : '0:00'}
                  timerStartedAt={entry?.timerStartedAt}
                  useDecimal={currentMember?.useDecimalTimeEntry ?? workspace?.useDecimalTimeEntry}
                  value={minutes}
                  disabled={disableMinutesInput}
                  onChange={(value) => setFieldValue('minutes', value)}
                />
              </Tooltip>
              {entry?.id && (
                <PlayPauseButton
                  disabled={disablePlayPauseButton}
                  isAnchor
                  isActive={!!entry.timerStartedAt}
                  onClick={onPlayPause.bind(this, entry)}>
                  {entry.timerStartedAt ? (
                    <Icon icon={entry.startTime && !trackTimeByDuration ? 'stop' : 'pause'} />
                  ) : (
                    <Icon icon={'play'} />
                  )}
                  {disablePlayPauseButton && <InlineTooltip message={conditionals.disableTimer} />}
                </PlayPauseButton>
              )}
            </TimeContainer>
          </Field>
          <Field name="typeId" errors={errors}>
            {conditionals.typeSelectDisabledTooltip || isProjectDisabled ? (
              <Tooltip message={conditionals.typeSelectDisabledTooltip}>
                <ReadTextbox label="Type" value={entry?.type.name || 'Project Time'} />
              </Tooltip>
            ) : (
              <SingleSelect
                name="typeId"
                placeholder="Type"
                value={typeId}
                onChange={(event) => setFieldValue('typeId', event.target.value)}>
                <option value="project_time">Project Time</option>
                <option value="time_off" disabled={!member.timeOffAllowed}>
                  Time Off
                </option>
              </SingleSelect>
            )}
          </Field>
        </Form.Control>
        {!!conditionals.timeOffTypeId && (
          <Form.Control>
            <Field name="timeOffTypeId" errors={errors}>
              <TimeOffTypeSelect
                name="timeOffTypeId"
                placeholder="Time Off Type"
                memberId={memberId}
                initialTimeOffType={entry?.timeOffType}
                value={timeOffTypeId}
                onChange={(event) => setFieldValue('timeOffTypeId', event.target.value)}
              />
            </Field>
          </Form.Control>
        )}
        {!!conditionals.projectId && (
          <Form.Control>
            <Field name="projectId" errors={errors}>
              {conditionals.projectSelectDisabledTooltip || isProjectDisabled ? (
                <Tooltip message={conditionals.projectSelectDisabledTooltip}>
                  <ReadTextbox
                    label="Project"
                    value={entry?.project?.name || (project && `${project.client.name} / ${project.name}`)}
                  />
                </Tooltip>
              ) : (
                <ProjectSelect
                  name="projectId"
                  placeholder="Project"
                  memberId={memberId}
                  initialProject={project || entry?.project}
                  value={projectId}
                  onChange={(event) => {
                    setFieldValue('projectId', event.target.value);
                    setFieldValue('projectRoleId', '');
                    setFieldValue('projectTaskId', '');
                  }}
                />
              )}
            </Field>
          </Form.Control>
        )}
        {!!conditionals.projectRoleId && (
          <Form.Control>
            <Field name="projectRoleId" errors={errors}>
              <RoleSelect
                name="projectRoleId"
                placeholder="Role"
                initialRole={role || entry?.role}
                projectId={projectId}
                memberId={memberId}
                value={projectRoleId}
                onChange={(event) => setFieldValue('projectRoleId', event.target.value)}
                shouldAutoAssignRole={isNew || !!entry?.role || projectId !== entry?.project?.id}
              />
            </Field>
          </Form.Control>
        )}
        {!!conditionals.projectTaskId && (
          <Form.Control>
            <Field name="projectTaskId" errors={errors}>
              <TaskSelect
                name="projectTaskId"
                placeholder="Task"
                initialTask={task || entry?.task}
                projectId={projectId}
                projectRoleId={projectRoleId}
                memberId={memberId}
                value={projectTaskId}
                onChange={(event) => setFieldValue('projectTaskId', event.target.value)}
              />
            </Field>
          </Form.Control>
        )}
        {hasCalendarPicker && (
          <MicrosoftCalendarEventPicker
            date={date}
            timezone={timezone}
            canLogTimeOff={canLogTimeOff}
            selectedEventId={microsoftEventId}
            excludeTimeEntryId={entry?.id}
            onSelect={handlePickCalendarEvent}
            onClear={handleClearCalendarEvent}
            setFieldValue={setFieldValue}
          />
        )}
        <Form.Control>
          <Field name="notes" errors={errors}>
            <TextArea
              name="notes"
              placeholder={`Notes ${conditionals.requireNotes ? '(required)' : ''}`}
              materialPlaceholder="Notes"
              rows={8}
              value={notes}
              onChange={(event) => setFieldValue('notes', event.target.value)}
            />
          </Field>
        </Form.Control>
      </Form.Section>
      {typeId === 'project_time' && projectTaskId && (
        <TaskDetails
          taskId={projectTaskId}
          memberId={entry?.memberId ?? memberId ?? member.id}
          projectRoleId={projectRoleId}
          entryRoleWorkedMinutes={
            entry?.task?.id === projectTaskId && entry?.role?.id === projectRoleId ? entry.minutes || 0 : 0
          }
          entryMinutes={Math.max(parseInt(minutes) || 0, 0)}
          entryWorkedMinutes={entry?.task?.id === projectTaskId ? entry.minutes || 0 : 0}
        />
      )}
    </div>
  );
}
